import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ColorService } from '../../service/color/color.service';
import { URLS } from '../../data/navigation/navigation.data';

// The hero card from the colour tool, packaged as a reusable widget: a colour
// swatch + editable hex on the left and the colour's identity (name, RGB, HSL,
// HSV, web-safe) on the right. Used on the colour tool (driven by a `hex` input)
// and standalone on the home page (seeds a random named colour + a CTA linking
// to the full colour tool).
@Component( {
  selector: 'app-color-widget',
  templateUrl: './color-widget.component.html',
  styleUrls: [ './color-widget.component.scss' ],
} )
export class ColorWidgetComponent implements OnInit, OnChanges {
  // Controlling colour from the parent (the colour tool passes its selection).
  // When omitted, the widget seeds itself with a random named colour.
  @Input() hex = '';
  // Show the "Get full details of <name>" link (home page only).
  @Input() showDetailsCta = false;
  // Emits the new '#rrggbb' whenever the user picks/types a valid colour.
  @Output() colorChange = new EventEmitter<string>();

  // Internal live colour (never mutate the @Input directly, that fights the
  // parent's [hex] binding and makes the inputs janky). `current` is the source
  // of truth the template binds to; `committedHex` mirrors it for the echo guard.
  current = '';
  hexText = '';
  colorName = '';
  formattedColorName = '';
  hsl: { h: number; s: number; l: number } = { h: 0, s: 0, l: 0 };
  temperature = '';
  rgbString = '';
  hslString = '';
  webSafe = '';
  /** more of the colour's codes under Web Safe, so the list fills the card (owner 2026-10-13) */
  moreRows: { label: string; value: string; key: string }[] = [];
  copiedKey = '';
  private copiedTimer: any;

  constructor( private colorService: ColorService ) {}

  ngOnInit(): void {
    if ( !this.hex ) {
      // Standalone (home page): start with a random named colour, like the tool.
      // (When [hex] is bound, ngOnChanges has already initialised it.)
      const name = this.colorService.getRandomNamedColor();
      const code = this.colorService.getHexCode( name );
      this.setHex( '#' + ( code || 'ffffff' ).toLowerCase(), false, true );
      // tell the host the colour it starts on, so the home page's Open Color Explorer opens on it (owner
      // 2026-10-12); after this check, so the host's binding does not change mid change detection
      Promise.resolve().then( () => this.colorChange.emit( this.current ) );
    }
  }

  ngOnChanges( changes: SimpleChanges ): void {
    // React only to a genuinely external colour (e.g. a scheme swatch click on
    // the colour tool). Skip the echo of our own emit so typing is never fought.
    if ( changes[ 'hex' ] && this.hex && this.hex !== this.current ) {
      this.setHex( this.hex, false, true );
    }
  }

  // Native colour picker (a deliberate full change, so sync the text field).
  onColorChange( value: string ) {
    this.setHex( ( value || '' ).toLowerCase(), true, true );
  }

  // Hex text field, sanitised exactly like the colour tool's hero input.
  onHexTextChange( value: string ) {
    const raw = ( value || '' ).replace( /[^0-9a-fA-F]/g, '' ).toLowerCase().slice( 0, 6 );
    this.hexText = raw;
    // Only commit a full six-digit hex (no three-char shorthand).
    if ( raw.length !== 6 || !this.colorService.isValidHexCode( raw ) ) {
      return;
    }
    this.setHex( '#' + raw, true, false );
  }

  onHexEnter( event: Event ) {
    ( event.target as HTMLInputElement ).blur();
  }

  onHexFocus( event: Event ) {
    const input = event.target as HTMLInputElement;
    setTimeout( () => {
      const end = input.value.length;
      input.setSelectionRange( end, end );
    } );
  }

  onHexKeydown( event: KeyboardEvent ) {
    if ( event.ctrlKey || event.metaKey ) {
      return;
    }
    if ( event.key && event.key.length === 1 && !/[0-9a-fA-F]/.test( event.key ) ) {
      event.preventDefault();
    }
  }

  copy( text: string, key: string ) {
    try {
      navigator.clipboard.writeText( text );
    } catch ( e ) {
      // Clipboard API unavailable; silently ignore.
    }
    this.copiedKey = key;
    if ( this.copiedTimer ) {
      clearTimeout( this.copiedTimer );
    }
    this.copiedTimer = setTimeout( () => ( this.copiedKey = '' ), 1200 );
  }

  // Route to the full colour tool for the current colour.
  get detailsRoute(): string {
    return URLS.tools.color + '/' + ( this.current || '' ).replace( '#', '' ).toLowerCase();
  }

  private setHex( hex: string, emit: boolean, syncText: boolean ) {
    this.current = hex;
    if ( syncText ) {
      this.hexText = hex.substring( 1 ).toLowerCase();
    }
    this.recompute( hex );
    if ( emit ) {
      this.colorChange.emit( hex );
    }
  }

  // Identity only, never touches hexText, so it can't fight the user's typing.
  private recompute( hex: string ) {
    this.colorName = this.colorService.getColorName( hex.substring( 1 ) );
    this.formattedColorName = new TitleCasePipe().transform( this.colorName.replace( /-/g, ' ' ) );
    const rgb = this.colorService.hexToRgb( hex );
    const r = rgb.items[ 0 ]?.value;
    const g = rgb.items[ 1 ]?.value;
    const b = rgb.items[ 2 ]?.value;
    this.hsl = this.colorService.hexToHsl( hex );
    this.temperature = this.colorService.getColorTemperature( hex );
    this.rgbString = `rgb(${ r }, ${ g }, ${ b })`;
    this.hslString = `hsl(${ this.hsl.h }, ${ this.hsl.s }%, ${ this.hsl.l }%)`;
    this.webSafe = this.colorService.nearestWebSafe( hex );
    const hsv = this.colorService.hexToHsv( hex );
    const [ hh, hw, hb ] = this.colorService.hexToHwb( hex );
    const cmyk = this.colorService.hexToCmyk( hex ).items;
    const [ ol, oc, oh ] = this.colorService.hexToOklch( hex );
    this.moreRows = [
      { label: 'HSV', value: `hsv(${ hsv[ 0 ] }, ${ hsv[ 1 ] }%, ${ hsv[ 2 ] }%)`, key: 'id-hsv' },
      { label: 'HWB', value: `hwb(${ hh } ${ hw }% ${ hb }%)`, key: 'id-hwb' },
      { label: 'CMYK', value: `cmyk(${ cmyk[ 0 ]?.value }%, ${ cmyk[ 1 ]?.value }%, ${ cmyk[ 2 ]?.value }%, ${ cmyk[ 3 ]?.value }%)`, key: 'id-cmyk' },
      { label: 'OKLCH', value: `oklch(${ ol } ${ oc } ${ oh })`, key: 'id-oklch' },
    ];
  }
}
